/**
 * TradingView Advanced Chart — live Binance symbol via our /api/spot/tradingview mapping.
 */
import { useEffect, useId, useRef, useState } from "react";
import { api } from "@/lib/api";

const TV_INTERVAL = {
  "1m": "1",
  "5m": "5",
  "15m": "15",
  "30m": "30",
  "1h": "60",
  "4h": "240",
  "1d": "D",
  "1w": "W",
  "1M": "M",
};

export default function TradingViewChart({ base, quote, interval = "15m", height = "100%" }) {
  const reactId = useId().replace(/:/g, "");
  const containerId = `tv_chart_${reactId}`;
  const wrapRef = useRef(null);
  const [symbol, setSymbol] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    let cancelled = false;
    setErr("");
    api
      .get(`/api/spot/tradingview/${base}/${quote}`)
      .then(({ data }) => {
        if (!cancelled) setSymbol(data.tradingview_symbol);
      })
      .catch(() => {
        if (!cancelled) setSymbol(`BINANCE:${base}${quote}`);
      });
    return () => { cancelled = true; };
  }, [base, quote]);

  useEffect(() => {
    if (!symbol || !wrapRef.current) return undefined;

    const el = document.getElementById(containerId);
    if (el) el.innerHTML = "";

    const mount = () => {
      if (!window.TradingView?.widget || !document.getElementById(containerId)) return;
      // eslint-disable-next-line no-new
      new window.TradingView.widget({
        autosize: true,
        symbol,
        interval: TV_INTERVAL[interval] || "15",
        timezone: "Asia/Kolkata",
        theme: "dark",
        style: "1",
        locale: "en",
        toolbar_bg: "#0f172a",
        enable_publishing: false,
        hide_top_toolbar: false,
        hide_legend: false,
        save_image: true,
        allow_symbol_change: false,
        studies: [],
        container_id: containerId,
        disabled_features: ["header_symbol_search", "symbol_search_hot_key"],
      });
    };

    if (window.TradingView?.widget) {
      mount();
      return undefined;
    }

    let script = document.querySelector('script[data-tv="1"]');
    if (!script) {
      script = document.createElement("script");
      script.src = "https://s3.tradingview.com/tv.js";
      script.async = true;
      script.dataset.tv = "1";
      script.onload = mount;
      script.onerror = () => setErr("TradingView script failed to load");
      document.head.appendChild(script);
    } else {
      script.addEventListener("load", mount);
    }

    return () => {
      script?.removeEventListener("load", mount);
      const node = document.getElementById(containerId);
      if (node) node.innerHTML = "";
    };
  }, [symbol, interval, containerId]);

  return (
    <div ref={wrapRef} className="w-full h-full min-h-[320px] relative" data-testid="tradingview-chart">
      {err ? (
        <div className="absolute inset-0 flex items-center justify-center text-[13px] text-slate-400">
          {err}
        </div>
      ) : null}
      {!symbol ? (
        <div className="absolute inset-0 flex items-center justify-center text-[13px] text-slate-500">
          Loading chart…
        </div>
      ) : null}
      <div id={containerId} style={{ width: "100%", height }} />
    </div>
  );
}
